import { useCallback, useState } from 'react';
import { careChapters } from '../content';
import { petAssetById } from '../petAssets';
import { useCareJourney } from '../hooks/useCareJourney';
import { PetCanvas } from '../components/PetCanvas';
import { PetParallaxCutout } from '../components/PetParallaxCutout';

export function CareJourneySection() {
  const [canvasProgress, setCanvasProgress] = useState(0);

  // The canvas damps toward this value, so 1% steps are imperceptible and cost
  // ~100 renders across the pinned scroll instead of one per frame.
  const handleProgress = useCallback((progress: number) => {
    const quantized = Math.round(progress * 100) / 100;
    setCanvasProgress((current) => (current === quantized ? current : quantized));
  }, []);

  const { sectionRef, pinRef, copyRef, activeChapter, prefersReducedMotion } = useCareJourney({
    chapterCount: careChapters.length,
    onProgress: handleProgress,
  });

  const activePet = petAssetById[careChapters[activeChapter]?.petId ?? 'dog-02'];

  return (
    <section id="care" ref={sectionRef} className="relative bg-surface" aria-labelledby="care-heading">
      <div
        ref={pinRef}
        className={`relative mx-auto grid max-w-[var(--container-max)] grid-cols-1 gap-6 px-[var(--section-padding-x)] lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 ${
          prefersReducedMotion
            ? 'py-[var(--section-padding-y)]'
            : // Pinned, the whole chapter has to fit between the sticky banner
              // and header above and the call dock below, or the copy a
              // visitor is scrubbing through sits behind the dock. On a phone
              // the height comes from the content, not the viewport: sized by
              // `svh`, the gate's software-rendered Lighthouse run spent 4-5s
              // re-rasterising the page and scored mobile 0.70 instead of 0.99.
              'content-start pb-24 pt-[calc(var(--demo-banner-height)+6.25rem)] lg:min-h-[100svh] lg:content-center lg:pb-8'
        }`}
      >
        <div
          className={`stage-frame w-full justify-self-center ${
            prefersReducedMotion
              ? 'aspect-[4/5] max-w-xl lg:max-w-none'
              : 'aspect-[4/5] max-w-[13rem] lg:aspect-[4/5] lg:h-[calc(100svh-var(--demo-banner-height)-9.25rem)] lg:w-auto lg:max-w-full'
          }`}
        >
          <div className="stage-frame-inner" aria-hidden="true" />
          <div className="film-grain z-[3]" aria-hidden="true" />
          <PetParallaxCutout
            key={activePet.id}
            src={activePet.srcSm}
            // The stage draws wider than the -sm cutouts at desktop and on dense
            // phone screens. The full file is the canvas texture too, so picking
            // it here is usually a cache hit rather than a second download.
            srcSet={`${activePet.srcSm} ${activePet.width}w, ${activePet.src} ${activePet.fullWidth}w`}
            sizes="(min-width: 1024px) 36vw, 70vw"
            width={activePet.width}
            height={activePet.height}
            className="z-[1]"
            scrollProgress={prefersReducedMotion ? 0 : canvasProgress}
            loading="lazy"
          />
          <PetCanvas
              className="z-[2]"
              overlay
              textureSrc={activePet.src}
              scrollProgress={prefersReducedMotion ? 0 : canvasProgress}
            pointerEnabled={!prefersReducedMotion}
          />
          <p className="absolute bottom-4 left-5 z-[4] font-body text-xs uppercase tracking-[0.28em] text-white/70">
            Chapter {String(activeChapter + 1).padStart(2, '0')}
          </p>
        </div>

        <div ref={copyRef} className="relative">
          <p className="section-eyebrow">The consult</p>
          <h2 id="care-heading" className="section-title">
            From the door to a plan you trust
          </h2>

          {prefersReducedMotion ? (
            <div className="mt-10 space-y-10">
              {careChapters.map((chapter, index) => (
                <article key={chapter.id} data-chapter={chapter.id}>
                  <span className="font-body text-xs uppercase tracking-[0.3em] text-primary-600">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{chapter.title}</h3>
                  <p className="section-copy max-w-lg">{chapter.body}</p>
                </article>
              ))}
            </div>
          ) : (
            <>
              <div className="relative mt-4 min-h-[11rem] lg:mt-10 lg:min-h-[17rem]">
                {careChapters.map((chapter, index) => {
                  const isActive = index === activeChapter;

                  return (
                    <article
                      key={chapter.id}
                      data-chapter={chapter.id}
                      className="absolute inset-0 transition-[opacity,transform] duration-300 ease-in-out"
                      style={{
                        opacity: isActive ? 1 : 0,
                        transform: `translateY(${isActive ? 0 : 20}px)`,
                        visibility: isActive ? 'visible' : 'hidden',
                        pointerEvents: isActive ? 'auto' : 'none',
                      }}
                      aria-hidden={!isActive}
                    >
                      <span className="font-body text-xs uppercase tracking-[0.3em] text-primary-600">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                        {chapter.title}
                      </h3>
                      <p className="section-copy max-w-lg text-lg">{chapter.body}</p>
                    </article>
                  );
                })}
              </div>

              <div className="mt-4 flex gap-2 lg:mt-10" aria-hidden="true">
                {careChapters.map((chapter, index) => (
                  <span
                    key={chapter.id}
                    className={`h-0.5 flex-1 transition-colors duration-300 ${
                      index <= activeChapter ? 'bg-primary-500' : 'bg-neutral-200'
                    }`}
                  />
                ))}
              </div>

              <p className="mt-6 hidden font-body text-xs uppercase tracking-[0.28em] text-neutral-500 lg:block">
                Scroll to scrub the consult
              </p>
            </>
          )}

          <p className="sr-only" aria-live="polite">
            Chapter {activeChapter + 1} of {careChapters.length}: {careChapters[activeChapter]?.title}
          </p>
        </div>
      </div>
    </section>
  );
}
